import { useMutation, useQuery } from '@apollo/client'
import { AutoAwesome, PriorityHigh } from '@mui/icons-material'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import {
  Button,
  ButtonGroup,
  ClickAwayListener,
  Grow,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  Stack,
  TableCell,
  TableRow,
  Typography,
} from '@mui/material'
import { uniq } from 'lodash'
import React, { useContext } from 'react'
import Markdown from 'react-markdown'

import IncompleteTaskInsight from '@/components/insights/IncompleteTaskInsight'
import InsightDialog from '@/components/insights/Insight.dialog'
import PriceMismatchInsight from '@/components/insights/PriceMismatchInsight'
import ProductNotSoldInsight from '@/components/insights/ProductNotSoldInsight'
import RecommendedSwapInsight from '@/components/insights/RecommendedSwapInsight'
import RevenueOutlierInsight from '@/components/insights/RevenueOutlierInsight'
import { InsightMetadata } from '@/components/insights/types'
import NotificationAlert from '@/components/notification/NotificationAlert'
import { NotificationContext } from '@/contexts/NotificationContext'
import { useRefresh } from '@/contexts/RefreshContext'
import {
  ACKNOWLEDGE_STORE_INSIGHT,
  AcknowledgeStoreInsightMutation,
  AcknowledgeStoreInsightMutationVariables,
  AiInsightOrder,
  GET_TENANT_AI_INSIGHTS,
  GetTenantAiInsightsQuery,
  GetTenantAiInsightsQueryVariables,
  IncompleteRestockMetadataFragment,
  IncompleteTaskMetadataFragment,
  InsightPriority,
  PriceMismatchMetadataFragment,
  ProductNotSoldMetadataFragment,
  RecommendedSwapMetadataFragment,
  RevenueOutlierMetadataFragment,
  SortDirection,
  StoreInsightType,
} from '@/graphql'
import { useTableOrder } from '@/hooks/useTableOrder'
import { useTablePageState } from '@/hooks/useTablePageState'
import { useApiLanguage } from '@/i18n/useApiLanguage'
import useT from '@/i18n/useT'

import DeliciousTable, { HeadCell } from '../../components/DeliciousTable'

export type AIInsightRow = {
  priority: InsightPriority
  store: string
  storeId: string
  startDate: string
  endDate: string
  ids: string[]
  summary: string
  type: StoreInsightType
  resolution: React.FC<{
    onClick: () => void
    onAcknowledge: () => void
  }>
  metadata: Array<InsightMetadata>
}

const renderSelectedInsight = (
  metadata: InsightMetadata,
  insightType: StoreInsightType,
): React.ReactNode | null => {
  switch (insightType) {
    case StoreInsightType.REVENUE_OUTLIER:
      return (
        <RevenueOutlierInsight
          metadata={metadata as RevenueOutlierMetadataFragment}
        />
      )
    case StoreInsightType.RECOMMENDED_SWAP:
      return (
        <RecommendedSwapInsight
          metadata={metadata as RecommendedSwapMetadataFragment}
        />
      )
    // Deprecated
    case StoreInsightType.INCOMPLETE_RESTOCK:
    case StoreInsightType.INCOMPLETE_TASK:
      return (
        <IncompleteTaskInsight
          metadata={
            metadata as
              | IncompleteRestockMetadataFragment
              | IncompleteTaskMetadataFragment
          }
        />
      )
    case StoreInsightType.PRICE_MISMATCH:
      return (
        <PriceMismatchInsight
          metadata={metadata as PriceMismatchMetadataFragment}
        />
      )
    case StoreInsightType.PRODUCT_NOT_SOLD:
      return (
        <ProductNotSoldInsight
          metadata={metadata as ProductNotSoldMetadataFragment}
        />
      )
    default:
      return null
  }
}

const ActionButton: React.FC<{
  onClick: () => void
  onAcknowledge: () => void
}> = ({ onClick, onAcknowledge }) => {
  const { t } = useT()
  const options = [t('aiInsightsTable.acknowledge')]
  const [open, setOpen] = React.useState(false)
  const anchorRef = React.useRef<HTMLDivElement>(null)

  const handleMenuItemClick = () => {
    onAcknowledge()
    setOpen(false)
  }
  const handleToggle = () => {
    setOpen(prevOpen => !prevOpen)
  }
  const handleClose = (event: Event) => {
    if (
      anchorRef.current &&
      anchorRef.current.contains(event.target as HTMLElement)
    ) {
      return
    }
    setOpen(false)
  }

  return (
    <>
      <ButtonGroup
        variant="contained"
        color={'secondary'}
        size={'small'}
        ref={anchorRef}
      >
        <Button onClick={onClick}>{t('aiInsightsTable.actionButton')}</Button>
        <Button onClick={handleToggle}>
          <ArrowDropDownIcon />
        </Button>
      </ButtonGroup>
      <Popper
        sx={{ zIndex: 1 }}
        open={open}
        anchorEl={anchorRef.current}
        transition
        disablePortal
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin:
                placement === 'bottom' ? 'center top' : 'center bottom',
            }}
          >
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList autoFocusItem>
                  {options.map(option => (
                    <MenuItem key={option} onClick={handleMenuItemClick}>
                      {option}
                    </MenuItem>
                  ))}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  )
}

type Props = {
  storeIds?: string[]
}

const AIInsightsTable: React.FC<Props> = props => {
  const { storeIds } = props
  const [selectedInsight, setSelectedInsight] = React.useState<{
    store: string
    type: StoreInsightType
    metadata: Array<InsightMetadata>
  } | null>(null)
  const [acknowledgedIds, setAcknowledgedIds] = React.useState<string[]>([])
  const { t } = useT()
  const { currentLanguage } = useApiLanguage()
  const { setSuccess, setError } = useContext(NotificationContext)
  const { page, rowsPerPage, setPage, setRowsPerPage } = useTablePageState({
    initialRowsPerPage: 5,
  })
  const [acknowledgeStoreInsight] = useMutation<
    AcknowledgeStoreInsightMutation,
    AcknowledgeStoreInsightMutationVariables
  >(ACKNOWLEDGE_STORE_INSIGHT)
  const { order, handleRequestSort, orderBy } = useTableOrder<AiInsightOrder>(
    'priority',
    SortDirection.ASC,
  )
  const { data, loading, error, refetch } = useQuery<
    GetTenantAiInsightsQuery,
    GetTenantAiInsightsQueryVariables
  >(GET_TENANT_AI_INSIGHTS, {
    fetchPolicy: 'cache-and-network',
    variables: {
      filter: {
        storeIds,
        language: currentLanguage,
      },
      order: { [orderBy]: order },
      pagination: { limit: rowsPerPage, offset: rowsPerPage * page },
    },
  })

  const headers: HeadCell<AiInsightOrder>[] = [
    {
      id: 'priority',
      alignment: 'left',
      label: t('aiInsightsTable.priorityHeader'),
      sortKey: 'priority',
    },
    {
      id: 'store',
      alignment: 'left',
      label: t('aiInsightsTable.storeHeader'),
      sortKey: 'store',
    },
    {
      id: 'message',
      alignment: 'left',
      label: t('aiInsightsTable.messageHeader'),
    },
    {
      id: 'type',
      alignment: 'left',
      label: t('aiInsightsTable.typeHeader'),
      sortKey: 'type',
    },
    {
      id: 'action',
      alignment: 'left',
      label: t('aiInsightsTable.actionHeader'),
    },
  ]
  const rows = (data?.tenantAiInsights.results || [])
    .filter(
      insight =>
        !insight.metadata.every(meta =>
          acknowledgedIds.includes(meta.insightId),
        ),
    )
    .map(insight => ({
      priority: insight.priority,
      summary: insight.summary,
      type: insight.insightType,
      storeId: insight.store?.id || '',
      ids:
        insight.insightType === StoreInsightType.INCOMPLETE_TASK ||
        insight.insightType === StoreInsightType.INCOMPLETE_RESTOCK
          ? uniq(
              (
                insight.metadata as (
                  | IncompleteTaskMetadataFragment
                  | IncompleteRestockMetadataFragment
                )[]
              ).flatMap(m => m.missingActionIds),
            )
          : [],
      startDate: insight.startDate,
      endDate: insight.endDate,
      store: insight.store?.location?.name || '', // TODO: what to have as a backup?
      metadata: insight.metadata,
      resolution: ActionButton,
    }))
  const acknowledgedRows = (data?.tenantAiInsights.results || []).filter(
    insight =>
      insight.metadata.every(meta => acknowledgedIds.includes(meta.insightId)),
  )

  const renderRow = (row: AIInsightRow, index: number) => {
    const priorityMap = {
      [InsightPriority.HIGH]: 3,
      [InsightPriority.MEDIUM]: 2,
      [InsightPriority.LOW]: 1,
    }
    return (
      <TableRow key={index} hover tabIndex={-1} role="checkbox">
        <TableCell width={100}>
          <Stack direction="row" spacing={-2} ml={-2}>
            {Array.from({ length: priorityMap[row.priority] }).map((_, idx) => (
              <PriorityHigh
                key={`priority-icon-${idx}`}
                sx={theme => ({ color: theme.palette.error.dark })}
              />
            ))}
          </Stack>
        </TableCell>
        <TableCell>
          <Typography variant="body2">{row.store}</Typography>
        </TableCell>
        <TableCell>
          <Typography variant="body2" component="div">
            <Markdown>{row.summary}</Markdown>
          </Typography>
        </TableCell>
        <TableCell>
          <Typography variant="body2">
            {t('aiInsightsTable.insightType', { type: row.type })}
          </Typography>
        </TableCell>
        <TableCell>
          {
            <row.resolution
              onClick={() => {
                if (row.type === StoreInsightType.INCOMPLETE_TASK) {
                  window.open(
                    `/tasks?storeIds[]=${row.storeId}&startDate=${row.startDate}&endDate=${row.endDate}&initialFocusedActionIds[]=${row.ids}`,
                  )
                } else if (row.type === StoreInsightType.INCOMPLETE_RESTOCK) {
                  window.open(
                    `/stores/${row.storeId}/restocks?startDate=${row.startDate}&endDate=${row.endDate}`,
                  )
                } else {
                  setSelectedInsight({
                    store: row.store,
                    type: row.type,
                    metadata: row.metadata,
                  })
                }
              }}
              onAcknowledge={() => {
                const promises: Array<Promise<any>> = []
                for (const meta of row.metadata) {
                  const id = meta.insightId
                  promises.push(acknowledgeStoreInsight({ variables: { id } }))
                }
                Promise.all(promises)
                  .then(() => {
                    setAcknowledgedIds(prev => [
                      ...prev,
                      ...row.metadata.map(meta => meta.insightId),
                    ])
                    setSuccess(
                      t('aiInsightsTable.acknowledgedInsights', {
                        store: row.store,
                      }),
                    )
                  })
                  .catch(() => setError(t('common.error.standardErrorMessage')))
              }}
            />
          }
        </TableCell>
      </TableRow>
    )
  }
  const handleRefresh = () => {
    refetch()
    setAcknowledgedIds([])
  }
  useRefresh(handleRefresh)

  return (
    <>
      <NotificationAlert />
      {selectedInsight && (
        <InsightDialog
          title={`${selectedInsight.store} — ${t('aiInsightsTable.insightType', { type: selectedInsight.type })}`}
          open
          acknowledgedIds={acknowledgedIds}
          onClose={() => setSelectedInsight(null)}
          onAcknowledge={index => {
            const id = selectedInsight.metadata[index].insightId
            acknowledgeStoreInsight({ variables: { id } })
              .then(() => {
                setAcknowledgedIds(prev => [...prev, id])
                setSuccess(
                  t('aiInsightsTable.acknowledgedInsights', {
                    store: selectedInsight.store,
                  }),
                )
              })
              .catch(() => setError(t('common.error.standardErrorMessage')))
          }}
          metadata={selectedInsight.metadata}
          renderFrame={meta =>
            renderSelectedInsight(meta, selectedInsight.type)
          }
        />
      )}
      <DeliciousTable<AIInsightRow, AiInsightOrder>
        title={
          <Stack direction="row" spacing={1} alignItems="center">
            <AutoAwesome />
            <span>{t('aiInsightsTable.title')}</span>
          </Stack>
        }
        noDataMessage={t('aiInsightsTable.noData')}
        data={rows}
        headCells={headers}
        renderRow={renderRow}
        loading={loading}
        error={error?.message}
        totalCount={
          data
            ? Math.max(
                data.tenantAiInsights.totalCount - acknowledgedRows.length,
                0,
              )
            : 0
        }
        handleRequestSort={handleRequestSort}
        order={order === 'ASC' ? 'asc' : 'desc'}
        orderBy={orderBy}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={setPage}
        onRowsPerPageChange={setRowsPerPage}
      />
    </>
  )
}

export default AIInsightsTable
